import { Injectable } from '@angular/core'
import {
    addDoc,
    collection,
    collectionData,
    doc,
    docData,
    DocumentData,
    DocumentReference,
    Firestore,
    getDocs,
    limit,
    orderBy,
    query,
    Query,
    QueryDocumentSnapshot,
    serverTimestamp,
    startAfter,
    updateDoc,
    where,
} from '@angular/fire/firestore'
import { HttpClient } from '@angular/common/http'
import { Router } from '@angular/router'
import { AuthenticationService } from './authentication.service'
import { firstValueFrom, from, map, mergeMap, Observable, of, switchMap, toArray } from 'rxjs'
import { Client, FirestoreStructure, Invoice, InvoiceStatus } from '@parkupp/core'
import { ClientService } from './client.service'
import { MerchantService } from './merchant.service'
import { ParkingService } from './parking.service'
import { SubscriptionService } from './subscription.service'
import { EnterpriseContextService } from './enterprise-context.service'
import * as firestore from 'firebase/firestore'

@Injectable({
    providedIn: 'root',
})
export class InvoiceService {
    collection = collection(this.firestore, FirestoreStructure.INVOICES)

    constructor(
        private authSvc: AuthenticationService,
        public firestore: Firestore,
        private router: Router,
        private http: HttpClient,
        private clientService: ClientService,
        private merchantService: MerchantService,
        private parkingService: ParkingService,
        private subscriptionService: SubscriptionService,
        private enterpriseContext: EnterpriseContextService
    ) {}
    
    /**
     * Apply enterprise filter to a query if enterprise context is active
     */
    private applyEnterpriseFilter(q: Query<DocumentData>): Query<DocumentData> {
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        if (enterpriseKey) {
            return query(q, where('denormEnterpriseKey', '==', enterpriseKey))
        }
        return q
    }

    getDocRef(invoiceKey: string) {
        return doc(this.firestore, `${FirestoreStructure.INVOICES}/${invoiceKey}`)
    }

    async create(invoice: Invoice) {
        if (!invoice.dueDate) {
            invoice.dueDate = serverTimestamp() as firestore.Timestamp
        }

        return addDoc(this.collection, invoice.parse()).then((docRef) => {
            return { status: 'success', reference: docRef.id }
        })
    }

    async update(invoice: Invoice): Promise<void> {
        const invoiceRef = doc(this.collection, invoice.$key)
        await updateDoc(invoiceRef, invoice.parse())
    }

    async list(status?: InvoiceStatus, pageSize: number = 500): Promise<QueryDocumentSnapshot<DocumentData>[]> {
        let invoicesQuery = query(this.collection, orderBy('dueDate', 'desc'), limit(pageSize))

        invoicesQuery = this.applyEnterpriseFilter(invoicesQuery)

        if (status) {
            invoicesQuery = query(invoicesQuery, where('status', '==', status))
        }

        const snapshot = await getDocs(invoicesQuery)
        return snapshot.docs
    }

    async listPage(
        status?: InvoiceStatus,
        pageSize: number = 100,
        startAfterDoc?: QueryDocumentSnapshot<DocumentData> | null
    ): Promise<{ docs: QueryDocumentSnapshot<DocumentData>[]; hasMore: boolean }> {
        let invoicesQuery = query(this.collection, orderBy('dueDate', 'desc'), limit(pageSize + 1))

        invoicesQuery = this.applyEnterpriseFilter(invoicesQuery)

        if (status) {
            invoicesQuery = query(invoicesQuery, where('status', '==', status))
        }

        if (startAfterDoc) {
            invoicesQuery = query(invoicesQuery, startAfter(startAfterDoc))
        }

        const snapshot = await getDocs(invoicesQuery)
        const hasMore = snapshot.docs.length > pageSize
        const docs = hasMore ? snapshot.docs.slice(0, pageSize) : snapshot.docs
        return { docs, hasMore }
    }

    /** Full count for a status (cursor iteration; same filters as listPage). */
    async countAllForStatus(status?: InvoiceStatus): Promise<number> {
        let total = 0
        let cursor: QueryDocumentSnapshot<DocumentData> | undefined
        while (true) {
            const { docs, hasMore } = await this.listPage(status, 500, cursor)
            total += docs.length
            if (!hasMore || docs.length === 0) {
                break
            }
            cursor = docs[docs.length - 1]
        }
        return total
    }

    /** Sum amountTotalDue for all DUE invoices (enterprise filter applies). */
    async sumTotalDueValue(): Promise<number> {
        let sum = 0
        let cursor: QueryDocumentSnapshot<DocumentData> | undefined
        while (true) {
            const { docs, hasMore } = await this.listPage(InvoiceStatus.DUE, 500, cursor)
            for (const doc of docs) {
                const invoice = new Invoice(doc.data())
                sum += invoice.amountTotalDue || 0
            }
            if (!hasMore || docs.length === 0) {
                break
            }
            cursor = docs[docs.length - 1]
        }
        return sum
    }

    listPaginated(startAfterDoc?: QueryDocumentSnapshot<Invoice>, pageSize: number = 200, status?: InvoiceStatus): Observable<QueryDocumentSnapshot<DocumentData>[]> {
        let invoicesQuery = query(this.collection, orderBy('createdAt', 'desc'), limit(pageSize))

        if (startAfterDoc) {
            invoicesQuery = query(invoicesQuery, startAfter(startAfterDoc))
        }

        if (status) {
            invoicesQuery = query(invoicesQuery, where('status', '==', status))
        }

        return from(getDocs(invoicesQuery)).pipe(
            map((snapshot) => snapshot.docs) // Map from QuerySnapshot to an array of QueryDocumentSnapshots
        )
    }

    listNoLimit(fromDate?: Date, toDate?: Date): Observable<QueryDocumentSnapshot<DocumentData>[]> {
        let invoicesQuery = query(this.collection, orderBy('createdAt', 'desc'))

        if (fromDate) {
            invoicesQuery = query(invoicesQuery, where('createdAt', '>=', fromDate))
        }

        if (toDate) {
            invoicesQuery = query(invoicesQuery, where('createdAt', '<=', toDate))
        }

        if (status) {
            invoicesQuery = query(invoicesQuery, where('status', '==', status))
        }

        return from(getDocs(invoicesQuery)).pipe(
            map((snapshot) => snapshot.docs) // Map from QuerySnapshot to an array of QueryDocumentSnapshots
        )
    }

    listOverdueInvoices(enterpriseKey?: string | null): Observable<Invoice[]> {
        const today = new Date()
        let overdueInvoicesQuery = query(this.collection, where('status', '==', InvoiceStatus.DUE), where('dueDate', '<', today))
        
        // Apply enterprise filter
        const filterKey = enterpriseKey ?? this.enterpriseContext.currentEnterpriseKey
        if (filterKey) {
            overdueInvoicesQuery = query(overdueInvoicesQuery, where('denormEnterpriseKey', '==', filterKey))
        }
        
        return collectionData(overdueInvoicesQuery, { idField: '$key' }).pipe(map((invoices: any[]) => invoices.map((invoiceData) => new Invoice(invoiceData)))) as Observable<Invoice[]>
    }

    listDueInvoices(enterpriseKey?: string | null): Observable<Invoice[]> {
        let dueInvoicesQuery = query(this.collection, where('status', '==', InvoiceStatus.DUE))
        
        // Apply enterprise filter
        const filterKey = enterpriseKey ?? this.enterpriseContext.currentEnterpriseKey
        if (filterKey) {
            dueInvoicesQuery = query(dueInvoicesQuery, where('denormEnterpriseKey', '==', filterKey))
        }
        
        return collectionData(dueInvoicesQuery, { idField: '$key' }).pipe(map((invoices: any[]) => invoices.map((invoiceData) => new Invoice(invoiceData)))) as Observable<Invoice[]>
    }

    listInvoicesNotPaidOut(enterpriseKey?: string | null): Observable<Invoice[]> {
        let notPaidOutQuery = query(this.collection, where('status', '==', InvoiceStatus.PAID), where('paidOut', '==', false))
        
        // Apply enterprise filter
        const filterKey = enterpriseKey ?? this.enterpriseContext.currentEnterpriseKey
        if (filterKey) {
            notPaidOutQuery = query(notPaidOutQuery, where('denormEnterpriseKey', '==', filterKey))
        }
        
        return collectionData(notPaidOutQuery, { idField: '$key' }).pipe(map((invoices: any[]) => invoices.map((invoiceData) => new Invoice(invoiceData)))) as Observable<Invoice[]>
    }

    getStatic(invoiceRef: DocumentReference): Promise<Invoice> {
        return firstValueFrom(docData(invoiceRef, { idField: '$key' }).pipe(map((invoiceData) => new Invoice(invoiceData))))
    }

    get(invoiceKey: string, fields: { client?: boolean; merchant?: boolean; parking?: boolean; subscription?: boolean } = {}): Observable<any> {
        const invoiceDocRef = doc(this.firestore, `${FirestoreStructure.INVOICES}/${invoiceKey}`)

        return docData(invoiceDocRef, { idField: '$key' }).pipe(
            switchMap((invoice: any) => {
                // Start with an observable of the invoice data
                let data$ = of(invoice)

                // If 'client' field is requested and exists, chain the observable to add client data
                if (fields.client && invoice.clientRef) {
                    data$ = data$.pipe(switchMap((invoiceData) => this.clientService.get(invoice.clientRef.id).pipe(map((clientData) => ({ ...invoiceData, $client: clientData })))))
                }

                // Repeat for merchant
                if (fields.merchant && invoice.merchantRef) {
                    data$ = data$.pipe(switchMap((invoiceData) => this.merchantService.get(invoice.merchantRef.id).pipe(map((merchantData) => ({ ...invoiceData, $merchant: merchantData })))))
                }

                // Repeat for parking
                if (fields.parking && invoice.parkingRef) {
                    data$ = data$.pipe(switchMap((invoiceData) => this.parkingService.get(invoice.parkingRef.id).pipe(map((parkingData) => ({ ...invoiceData, $parking: parkingData })))))
                }

                // Repeat for subscription
                if (fields.subscription && invoice.subscriptionRef) {
                    data$ = data$.pipe(
                        switchMap((invoiceData) => this.subscriptionService.get(invoice.subscriptionRef.id).pipe(map((subscriptionData) => ({ ...invoiceData, $subscription: subscriptionData }))))
                    )
                }

                // Return the final observable
                return data$
            })
        )
    }
    async getInvoicesByPayout(payoutRef: string): Promise<Invoice[]> {
        const invoicesQuery = query(this.collection, where('payoutRef', '==', payoutRef))
        const snapshot = await getDocs(invoicesQuery)
        return snapshot.docs.map((doc) => new Invoice({ ...doc.data(), $key: doc.id }))
    }

    async getInvoicesByInvoiceRefs(invoiceRefs: DocumentReference[]): Promise<Invoice[]> {
        const invoices = await firstValueFrom(
            from(invoiceRefs).pipe(
                mergeMap((invoiceRef: any) => this.getStatic(invoiceRef)),
                toArray()
            )
        )
        return invoices
    }

    getInvoicesByClientRef(clientRef: string): Observable<Invoice[]> {
        // Adjust the 'createdAt' field name if your documents use a different field for sorting
        const invoicesQuery = query(
            this.collection,
            where('clientRef', '==', clientRef),
            orderBy('createdAt', 'desc') // Add this line, replace 'createdAt' with your field name
        )
        return collectionData(invoicesQuery, { idField: '$key' }).pipe(map((invoices: any[]) => invoices.map((invoiceData) => new Invoice(invoiceData)))) as Observable<Invoice[]>
    }

    getInvoicesByClientRefAndDateRange(clientRef: string, startDate: Date, endDate: Date): Observable<Invoice[]> {
        const invoicesQuery = query(this.collection, where('clientRef', '==', clientRef), where('createdAt', '>=', startDate), where('createdAt', '<=', endDate), orderBy('createdAt', 'desc'))
        return collectionData(invoicesQuery, { idField: '$key' }).pipe(map((invoices: any[]) => invoices.map((invoiceData) => new Invoice(invoiceData)))) as Observable<Invoice[]>
    }

    getInvoicesByMerchantRefAndDateRange(merchantRef: string, startDate: Date, endDate: Date): Observable<Invoice[]> {
        const invoicesQuery = query(this.collection, where('merchantRef', '==', merchantRef), where('createdAt', '>=', startDate), where('createdAt', '<=', endDate), orderBy('createdAt', 'desc'))
        return collectionData(invoicesQuery, { idField: '$key' }).pipe(map((invoices: any[]) => invoices.map((invoiceData) => new Invoice(invoiceData)))) as Observable<Invoice[]>
    }

    async cancelInvoice(invoice: Invoice) {
        invoice.status = InvoiceStatus.CANCELLED
        console.log(invoice)
        return await this.update(invoice)
    }

    async markAsPaid(invoice: Invoice) {
        invoice.status = InvoiceStatus.PAID
        invoice.paymentDate = serverTimestamp() as firestore.Timestamp

        await this.update(invoice)
    }

    async markAsClientBreeched(invoice: Invoice) {
        invoice.status = InvoiceStatus.BREECHED
        await this.update(invoice)
    }

    async markAsRefunded(invoice: Invoice) {
        invoice.status = InvoiceStatus.REFUNDED
        await this.update(invoice)
    }

    async markAsDue(invoice: Invoice) {
        invoice.status = InvoiceStatus.DUE
        await this.update(invoice)
    }

    async markAsCancelled(invoice: Invoice) {
        invoice.status = InvoiceStatus.CANCELLED
        await this.update(invoice)
    }

    async getInvoicesBySubscription(subscriptionKey: string): Promise<Invoice[]> {
        const invoicesQuery = query(this.collection, where('subscriptionRef', '==', subscriptionKey))
        const snapshot = await getDocs(invoicesQuery)
        return snapshot.docs.map((doc) => new Invoice({ ...doc.data(), $key: doc.id }))
    }
}
